import _ from 'lodash';
import moment from 'moment';
import Papa from 'papaparse';
import pluralize from 'pluralize';
import React, { memo, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Currency, Hours, Percent, RouteLink, Tooltip } from '~/components';
import { colors, weights } from '~/styles';
import { resolveLink } from './utils/resolveLink';
import Table from './Table';

const PREVIEW_ROWS = 8;

const fallbackCurrencyFormat = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fallbackNumberFormat = new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// --- Data arrangement ---
//
// The pivot arranges flat tool-returned rows into (row-dim-tuple × period) cells. It performs
// no aggregation — every value, label, ordering, and tooltip structure comes from the tool's
// data or its `render.pivot` config. If the tool didn't resolve it, the pivot can't display it.

export function pivotData({ data, fields, meta, cellValue, tooltipConfig }) {
  if (!Array.isArray(data) || data.length === 0) return null;

  const group = meta?.applied?.input?.group ?? [];
  const periodGroup = group.find((g) => g.field === 'period');
  if (!periodGroup) return null;

  const fieldMap = Object.fromEntries(fields.map((f) => [f.key, f]));

  // Row dimensions: contract-declared fields whose groupField matches a non-period group entry.
  const rowDimFields = group
    .filter((g) => g.field !== 'period')
    .flatMap((g) => fields.filter((f) => f.groupField === g.field && f.role === 'dimension' && f.visible !== false))
    .filter((f, i, arr) => arr.indexOf(f) === i);
  if (rowDimFields.length === 0) return null;

  // Period column key comes from the contract's time field for the period group.
  const periodField = fields.find((f) => f.role === 'time' && f.groupField === periodGroup.field);
  const periodKey = periodField?.key;
  if (!periodKey) return null;

  // cellValue resolution (LLM override vs contract default) happens upstream in the backend —
  // we just receive the resolved field key and look it up.
  const cellField = cellValue && fieldMap[cellValue];
  if (!cellField) return null;

  // Pivot is pure display — it only looks up tool-provided rows by rowType marker.
  // Tool may include 'rowSubtotal' (per row-dim tuple), 'periodSubtotal' (per period),
  // and 'total' (grand) rows. If the tool doesn't provide them, the corresponding
  // Total cells stay empty.
  const bodyRows = data.filter((r) => !r.rowType || r.rowType === 'group');

  const periods = [...new Set(bodyRows.map((r) => r[periodKey]).filter(Boolean))].sort();
  if (periods.length === 0) return null;

  const rowKeyOf = (row) => rowDimFields.map((f) => row[f.key] ?? '').join('|');

  const rowMap = new Map();
  for (const row of bodyRows) {
    const rowKey = rowKeyOf(row);
    if (!rowMap.has(rowKey)) {
      rowMap.set(rowKey, {
        key: rowKey,
        dimValues: rowDimFields.map((f) => row[f.key]),
        sampleRow: row,
        cells: new Map(),
      });
    }
    rowMap.get(rowKey).cells.set(row[periodKey], row);
  }

  const rowSubtotalByKey = new Map();
  for (const r of data.filter((r) => r.rowType === 'rowSubtotal')) {
    rowSubtotalByKey.set(rowKeyOf(r), r);
  }
  const periodSubtotalByPeriod = new Map();
  for (const r of data.filter((r) => r.rowType === 'periodSubtotal')) {
    if (r[periodKey] != null) periodSubtotalByPeriod.set(r[periodKey], r);
  }
  const grandTotalRow = data.find((r) => r.rowType === 'total') ?? null;

  return {
    rows: [...rowMap.values()],
    rowDimFields,
    periods,
    periodUnit: periodGroup.unit || 'month',
    periodKey,
    cellField,
    metricFields: fields.filter((f) => f.role === 'metric'),
    fieldMap,
    tooltipConfig: tooltipConfig ?? null,
    rowSubtotalByKey,
    periodSubtotalByPeriod,
    grandTotalRow,
  };
}

export function formatPeriod(periodStart, unit) {
  const d = moment(periodStart);
  switch (unit) {
    case 'year':
      return d.format('YYYY');
    case 'quarter':
      return `Q${d.quarter()} ${d.format('YYYY')}`;
    case 'month':
      return d.format('MMM YYYY');
    case 'week':
    case 'day':
    default:
      return d.format('MMM D');
  }
}

function formatMetric(value, field, contextRow) {
  if (value == null) return '';
  if (field.semantic === 'currency') {
    const currency = field.currencyPath ? _.get(contextRow, field.currencyPath) : undefined;
    if (currency) return <Currency value={value} currency={currency} />;
    return fallbackCurrencyFormat.format(value);
  }
  if (field.semantic === 'hours') return <TooltipHours value={value} />;
  if (field.semantic === 'percent') return <Percent value={value} />;
  if (field.type === 'number' || field.type === 'integer') return fallbackNumberFormat.format(value);
  return String(value);
}

// --- Styled components ---

const S = {
  Container: styled.div`
    margin: 0.75rem 0;
    overflow-anchor: none;
  `,

  TableScroll: styled.div`
    overflow-x: auto;
    overflow-y: hidden;
    position: relative;

    > table {
      min-width: 100%;
      border-collapse: collapse;
      font-size: 0.75rem;
      table-layout: auto;

      th,
      td {
        padding: 0.375rem 0.5rem;
        white-space: nowrap;
        border-right: 1px solid ${colors.grey5};
      }

      th:last-child,
      td:last-child {
        border-right: none;
      }

      th {
        font-weight: 600;
        background: ${colors.grey5};
        border-bottom: 2px solid ${colors.grey20};
        text-align: right;
      }

      td {
        border-bottom: 1px solid ${colors.grey10};
        text-align: right;
      }

      th.dim,
      td.dim {
        text-align: left;
        min-width: 10rem;
        position: sticky;
        left: 0;
        background: ${colors.white};
        z-index: 1;
        border-right: 1px solid ${colors.grey10};
      }

      th.dim {
        background: ${colors.grey5};
        z-index: 2;
      }

      th.period-center {
        text-align: center;
      }

      tr:hover td,
      tr:hover td.dim {
        background: ${colors.grey5};
      }

      td.blank {
        color: ${colors.grey40};
      }

      td.total-col,
      th.total-col {
        font-weight: 600;
        border-left: 2px solid ${colors.grey20};
      }

      .total-row td {
        font-weight: 600;
        border-top: 2px solid ${colors.grey20};
        background: ${colors.grey5};
      }

      .total-row td.dim {
        background: ${colors.grey5};
      }
    }
  `,

  DimStack: styled.div`
    line-height: 1.1;

    small {
      display: block;
      font-size: 0.625rem;
      color: ${colors.grey40};
      padding-top: 0.1875rem;
    }
  `,

  ButtonRow: styled.div`
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.375rem 0;
    opacity: 0.5;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  `,

  ToggleButton: styled.button`
    border: 1px solid ${colors.grey10};
    border-radius: 999rem;
    background: ${colors.white};
    color: ${colors.grey75};
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &:hover {
      background: ${colors.grey5};
      color: ${colors.black};
    }
  `,
};

// --- Tooltip ---

const TooltipGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ columnCount }) => `auto repeat(${columnCount}, minmax(4.5rem, 1fr))`};
  column-gap: 1rem;
  row-gap: 0.5rem;
  padding: 0.25rem 0;
  font-size: 0.75rem;
  align-items: center;
`;

const TooltipFlat = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 1rem;
  row-gap: 0.5rem;
  padding: 0.25rem 0;
  font-size: 0.75rem;
  align-items: center;
`;

const TooltipLabel = styled.div`
  color: ${colors.grey40};
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  font-size: 0.625rem;
`;

const TooltipHeader = styled(TooltipLabel)`
  text-align: right;
`;

const TooltipValue = styled.div`
  white-space: nowrap;
  text-align: right;
`;

function TooltipHours({ value }) {
  return (
    <>
      <Hours value={value} /> {_.isNumber(value) && pluralize('hours', value)}
    </>
  );
}

function renderCellByKey(key, fieldMap, dataRow, contextRow) {
  if (!key) return '';
  const field = fieldMap[key];
  if (!field) return '';
  const v = dataRow?.[key];
  if (v == null) return '';
  return formatMetric(v, field, contextRow);
}

function evaluateGroupWhen(when, contextRow) {
  if (!when) return true;
  if (when.fieldsDiffer) {
    const [a, b] = when.fieldsDiffer;
    return contextRow?.[a] !== contextRow?.[b];
  }
  return true;
}

function TooltipGridLayout({ config, fieldMap, dataRow, contextRow }) {
  const columns = config.columns ?? [];
  const groups = config.groups ?? [];
  const standalone = config.standalone ?? [];

  // Resolve each group's cells, then drop groups that either (a) the tool's `when`
  // predicate excludes for this cell context, or (b) have no resolvable values.
  const resolvedGroups = groups
    .filter((group) => evaluateGroupWhen(group.when, contextRow))
    .map((group) => ({
      label: group.label,
      cells: columns.map((_, ci) => renderCellByKey(group.cells?.[ci], fieldMap, dataRow, contextRow)),
    }))
    .filter((g) => g.cells.some((c) => c !== ''));

  return (
    <>
      {resolvedGroups.length > 0 && (
        <TooltipGrid columnCount={columns.length}>
          {columns.length > 0 && (
            <>
              <span />
              {columns.map((col) => (
                <TooltipHeader key={col}>{col}</TooltipHeader>
              ))}
            </>
          )}
          {resolvedGroups.map((group) => (
            <React.Fragment key={group.label}>
              <TooltipLabel>{group.label}</TooltipLabel>
              {group.cells.map((cell, ci) => (
                <TooltipValue key={columns[ci]}>{cell}</TooltipValue>
              ))}
            </React.Fragment>
          ))}
        </TooltipGrid>
      )}
      {standalone.length > 0 && (
        <TooltipFlat>
          {standalone.map((key) => {
            const field = fieldMap[key];
            if (!field) return null;
            return (
              <React.Fragment key={key}>
                <TooltipLabel>{field.label}</TooltipLabel>
                <TooltipValue>{renderCellByKey(key, fieldMap, dataRow, contextRow)}</TooltipValue>
              </React.Fragment>
            );
          })}
        </TooltipFlat>
      )}
    </>
  );
}

function TooltipFlatLayout({ metricFields, dataRow, contextRow }) {
  return (
    <TooltipFlat>
      {metricFields.map((f) => {
        const v = dataRow?.[f.key];
        return (
          <React.Fragment key={f.key}>
            <TooltipLabel>{f.label}</TooltipLabel>
            <TooltipValue>{v == null ? '' : formatMetric(v, f, contextRow)}</TooltipValue>
          </React.Fragment>
        );
      })}
    </TooltipFlat>
  );
}

function PivotTooltip({ config, metricFields, fieldMap, dataRow, contextRow }) {
  if (config?.groups?.length) {
    return <TooltipGridLayout config={config} fieldMap={fieldMap} dataRow={dataRow} contextRow={contextRow} />;
  }
  return <TooltipFlatLayout metricFields={metricFields} dataRow={dataRow} contextRow={contextRow} />;
}

// --- Cells ---

function PivotCell({ row, period, cellField, tooltipConfig, metricFields, fieldMap, renderCell }) {
  const contextRow = row.cells.get(period);
  // No data row at this (rowDim, period) intersection — render an empty placeholder.
  // Matches the original behaviour: undefined cellV → 'blank' class, '' display.
  if (!contextRow) return <td className="blank" />;

  const cellV = contextRow[cellField.key];
  // Default cell render is the cellField's `semantic` formatter (currency / percent /
  // hours / number). Tools can pass `renderCell(contextRow, cellField)` to fully
  // control the visual — used by timesheet-summary to draw a status icon.
  const display = renderCell
    ? renderCell(contextRow, cellField)
    : cellV == null
      ? ''
      : formatMetric(cellV, cellField, contextRow);
  // Only the default renderer dims null cells. A renderCell function is responsible
  // for its own empty-state styling.
  const className = !renderCell && cellV == null ? 'blank' : undefined;

  return (
    <td className={className}>
      <Tooltip
        delay={350}
        maxWidth="unset"
        message={
          <PivotTooltip
            config={tooltipConfig}
            metricFields={metricFields}
            fieldMap={fieldMap}
            dataRow={contextRow}
            contextRow={contextRow}
          />
        }>
        <span>{display}</span>
      </Tooltip>
    </td>
  );
}

function TotalCell({ subtotalRow, cellField, tooltipConfig, metricFields, fieldMap, className }) {
  if (!subtotalRow) return <td className={className} />;
  const v = subtotalRow[cellField.key];
  const display = v == null ? '' : formatMetric(v, cellField, subtotalRow);
  return (
    <td className={className}>
      <Tooltip
        delay={350}
        maxWidth="unset"
        message={
          <PivotTooltip
            config={tooltipConfig}
            metricFields={metricFields}
            fieldMap={fieldMap}
            dataRow={subtotalRow}
            contextRow={subtotalRow}
          />
        }>
        <span>{display}</span>
      </Tooltip>
    </td>
  );
}

function RowDimCell({ row, rowDimFields, workspaceKey }) {
  return (
    <td className="dim">
      <S.DimStack>
        {rowDimFields.map((f, i) => {
          const value = row.dimValues[i] ?? '';
          const link = f.link?.template ? resolveLink(f.link.template, row.sampleRow, workspaceKey) : null;
          const content = link ? <RouteLink to={link}>{value}</RouteLink> : value;
          return i === 0 ? <div key={f.key}>{content}</div> : <small key={f.key}>{content}</small>;
        })}
      </S.DimStack>
    </td>
  );
}

// --- Component ---

function AssistantPivot({
  data,
  fields,
  meta,
  pagination,
  filename,
  tooltipConfig,
  cellValue,
  renderCell,
  headerAlign = 'right',
}) {
  const pivot = useMemo(
    () => pivotData({ data, fields, meta, cellValue, tooltipConfig }),
    [data, fields, meta, cellValue, tooltipConfig],
  );
  const [isExpanded, setIsExpanded] = useState(false);

  if (!pivot) {
    return <Table data={data} fields={fields} meta={meta} pagination={pagination} filename={filename} />;
  }

  const {
    rows,
    rowDimFields,
    periods,
    periodUnit,
    cellField,
    metricFields,
    fieldMap,
    rowSubtotalByKey,
    periodSubtotalByPeriod,
    grandTotalRow,
  } = pivot;
  const effectiveTooltipConfig = pivot.tooltipConfig;
  const workspaceKey = meta?.workspaceKey;
  const canCollapse = rows.length > PREVIEW_ROWS;
  const visibleRows = isExpanded || !canCollapse ? rows : rows.slice(0, PREVIEW_ROWS);

  const showRowTotals = rowSubtotalByKey.size > 0;
  const showPeriodTotals = periodSubtotalByPeriod.size > 0 || grandTotalRow != null;

  const handleDownload = () => {
    const csvRows = [];
    for (const row of rows) {
      const csvRow = {};
      rowDimFields.forEach((f, i) => {
        csvRow[f.label] = row.dimValues[i] ?? '';
      });
      for (const period of periods) {
        csvRow[formatPeriod(period, periodUnit)] = row.cells.get(period)?.[cellField.key] ?? '';
      }
      csvRows.push(csvRow);
    }
    const csv = Papa.unparse(csvRows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename || 'pivot'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <S.Container>
      <S.TableScroll>
        <table>
          <thead>
            <tr>
              <th className="dim">{rowDimFields.map((f) => f.label).join(' · ')}</th>
              {periods.map((p) => (
                <th key={p} className={headerAlign === 'center' ? 'period-center' : undefined}>
                  {formatPeriod(p, periodUnit)}
                </th>
              ))}
              {showRowTotals && <th className="total-col">Total</th>}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.key}>
                <RowDimCell row={row} rowDimFields={rowDimFields} workspaceKey={workspaceKey} />
                {periods.map((p) => (
                  <PivotCell
                    key={p}
                    row={row}
                    period={p}
                    cellField={cellField}
                    tooltipConfig={effectiveTooltipConfig}
                    metricFields={metricFields}
                    fieldMap={fieldMap}
                    renderCell={renderCell}
                  />
                ))}
                {showRowTotals && (
                  <TotalCell
                    subtotalRow={rowSubtotalByKey.get(row.key)}
                    cellField={cellField}
                    tooltipConfig={effectiveTooltipConfig}
                    metricFields={metricFields}
                    fieldMap={fieldMap}
                    className="total-col"
                  />
                )}
              </tr>
            ))}
            {showPeriodTotals && (
              <tr className="total-row">
                <td className="dim">Total</td>
                {periods.map((p) => (
                  <TotalCell
                    key={p}
                    subtotalRow={periodSubtotalByPeriod.get(p)}
                    cellField={cellField}
                    tooltipConfig={effectiveTooltipConfig}
                    metricFields={metricFields}
                    fieldMap={fieldMap}
                  />
                ))}
                {showRowTotals && (
                  <TotalCell
                    subtotalRow={grandTotalRow}
                    cellField={cellField}
                    tooltipConfig={effectiveTooltipConfig}
                    metricFields={metricFields}
                    fieldMap={fieldMap}
                    className="total-col"
                  />
                )}
              </tr>
            )}
          </tbody>
        </table>
      </S.TableScroll>
      <S.ButtonRow>
        {canCollapse &&
          (isExpanded ? (
            <S.ToggleButton onClick={() => setIsExpanded(false)}>Show less ({rows.length} rows)</S.ToggleButton>
          ) : (
            <S.ToggleButton onClick={() => setIsExpanded(true)}>
              Show all ({rows.length - PREVIEW_ROWS} more rows)
            </S.ToggleButton>
          ))}
        <S.ToggleButton onClick={handleDownload}>Save CSV</S.ToggleButton>
      </S.ButtonRow>
    </S.Container>
  );
}

export default memo(AssistantPivot);
